import React from "react";
import { Controller } from "react-hook-form";
import {
  convertLabelIntoName,
  resolveDynamicFieldKey,
  dynamicFormDateValidation,
  findStoredFormSignature,
  getError,
  getExtensionName,
  parseBooleanVal,
  signatureImageSrc,
  visibleFormHtml,
} from "Utils/utils";
import {
  getRequiredTableError,
  TABLE_REQUIRED_FIELD_NAME,
} from "Utils/dynamicFormTableValidation";
import SignatureCanvas from "react-signature-canvas";
import { QuestionMarkCircleIcon } from "@heroicons/react/solid";
import { FiPlus } from "react-icons/fi";
import {
  EMAIL_REGEX_EXP,
  REGEX_PHONE_NUMBER,
  STATE_FULL_LABEL_OPTIONS,
} from "Constants/constant";
import CustomButton from "components/common/Button/CustomButton";
import SubSectionInputFields from "./SubSectionInputFields";
import {
  FormReadOnlyField,
  FormReadOnlyChoice,
  FormReadOnlyTable,
  FormReadOnlySignature,
} from "../shared/FormReadOnlyField";
import { previewFieldPlaceholder } from "../shared/formBuilderConstants";

const PARTICIPANT_NAME_FIELD = "participant_name";
const PARTICIPANT_NAME_LABEL = "participant name";

const assignSignatureCanvasRef = (sigCanvasManager, key, element) => {
  if (!sigCanvasManager?.current || !key) return;
  if (!sigCanvasManager.current[key]) {
    sigCanvasManager.current[key] = { current: null };
  }
  sigCanvasManager.current[key].current = element;
};

const isExactParticipantNameField = (field) => {
  const fieldName = String(field?.name || "").trim().toLowerCase();
  if (fieldName === PARTICIPANT_NAME_FIELD) return true;

  const label = String(field?.lable || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  return label === PARTICIPANT_NAME_LABEL;
};

const capitalizeParticipantName = (value) => {
  if (!value) return "";
  return String(value).replace(
    /(^|[^A-Za-z])([A-Za-z])/g,
    (_, prefix, letter) => prefix + letter.toUpperCase()
  );
};

const SectionInputFields = (props) => {
  const {
    isViewable = false,
    previewMode = false,
    section,
    register = null,
    setValue = null,
    control,
    watchFormData = null,
    index,
    errors,
    fileUploadHandler,
    fileUploadChecker,
    fileLoader,
    signatures,
    formatIntoPngManager,
    sigCanvasManager,
    clearSignatureManager,
    setSelectedLegal,
    delteRowHandler,
    addRowHandler,
    updatedBody,
    templateForm = null,
    setFieldName = null,
    participantList = [],
    setSelectedBody,
    setOpenBodyModal,
    participant = null,
    handleRemoveOther,
    handleAddOther,
  } = props;


  const checkIsDependent = (element) => {
    if (
      parseBooleanVal(element?.is_dependent_field) &&
      element?.dependent_field_name
    ) {
      const tempData = [];
      section.input_fields.forEach((field, fieldIndex) => {
        if (field.type !== "sub_section") {
          tempData.push({ ...field, subIndex: null, sectionIndex: fieldIndex });
        } else {
          field.sub_fields.forEach((subField, subIndex) => {
            tempData.push({ ...subField, subIndex, sectionIndex: fieldIndex });
          });
        }
      });
      
      if (tempData.length > 0) {
        const fieldIndex = tempData.findIndex(
          (y) => y.name === element.dependent_field_name
        );
        
        if (fieldIndex === -1) {
          return false;
        }

        const dependentFieldData = tempData[fieldIndex];
        const { sectionIndex, subIndex } = dependentFieldData || {};
        let dependentFieldValue = null;
        let dependentFieldType = null;

        // Check if watchFormData is available (edit mode)
        if (watchFormData && Object.keys(watchFormData).length > 0) {
          const { input_fields } = watchFormData?.template?.[index] || {};
          let isCheckForSectionOrSubSection = false;

          if (subIndex === null) {
            if (sectionIndex != null && Array.isArray(input_fields)) {
              const sectionField = input_fields[sectionIndex];
              if (
                sectionField &&
                typeof sectionField === "object" &&
                Object.keys(sectionField).length > 0
              ) {
                isCheckForSectionOrSubSection = sectionField;
              }
            }
          } else {
            if (sectionIndex != null && Array.isArray(input_fields)) {
              const sectionField = input_fields[sectionIndex];

              if (
                sectionField &&
                typeof sectionField === "object" &&
                sectionField.sub_section &&
                Array.isArray(sectionField.sub_section)
              ) {
                const subSection = sectionField.sub_section[subIndex];
                if (
                  subSection &&
                  typeof subSection === "object" &&
                  Object.keys(subSection).length > 0
                ) {
                  isCheckForSectionOrSubSection = subSection;
                }
              }
            }
          }

          if (isCheckForSectionOrSubSection) {
            const name = dependentFieldData.name;
            dependentFieldValue = Object.values(isCheckForSectionOrSubSection)[0];
            dependentFieldType =
              subIndex === null
                ? section?.input_fields[sectionIndex]?.type
                : section?.input_fields[sectionIndex]?.sub_fields[subIndex]?.type;
          }
        } else {
          // View mode: use actual field values from section data
          if (subIndex === null) {
            // Regular field in section
            const field = section?.input_fields[sectionIndex];
            if (field) {
              dependentFieldValue = field.value;
              dependentFieldType = field.type;
            }
          } else {
            // Field in sub-section
            const subSectionField = section?.input_fields[sectionIndex];
            if (subSectionField && subSectionField.sub_fields) {
              const subField = subSectionField.sub_fields[subIndex];
              if (subField) {
                dependentFieldValue = subField.value;
                dependentFieldType = subField.type;
              }
            }
          }
        }

        // Compare the dependent field value with expected value
        if (dependentFieldValue !== null && dependentFieldValue !== undefined) {
          if (dependentFieldType === "checkbox") {
            return (
              parseBooleanVal(dependentFieldValue) ===
              parseBooleanVal(element.dependent_field_value)
            );
          }
          return (
            String(dependentFieldValue) === String(element.dependent_field_value)
          );
        }

        return false;
      }
    }
    return true;
  };

  return (
    <>
      {section?.input_fields.length > 0 &&
        section?.input_fields.map((field, i) => {
          const inputKey = resolveDynamicFieldKey(field, i);
          const isReadOnlyView = register === null && isViewable;

          // if (field.type === "content" && checkIsDependent(field)) {
          //   return (
          //     <div
          //       key={`${convertLabelIntoName(field.type)}-${i}`}
          //       className={`col-span-12 flex flex-col`}
          //     >
          //       {register === null &&
          //         field.hasOwnProperty("content") &&
          //         field.content && (
          //           <div
          //             // className="text-xs text-gray-600 rounded-lg px-2 py-2"
          //             className='py-2 border p-4 rounded-lg'
          //             dangerouslySetInnerHTML={{
          //               __html: field.content,
          //             }}
          //           />
          //         )}
          //       {register && (
          //         <TextEditor
          //           text={field.content}
          //           setText={(e) =>
          //             setValue(
          //               `template[${index}].input_fields[${i}].${convertLabelIntoName(
          //                 field.type
          //               )}`,
          //               e
          //             )
          //           }
          //         />
          //       )}
          //     </div>
          //   );
          // }
          if (field.type === "content" && checkIsDependent(field))
          {
            const contentHtml = visibleFormHtml(field.content);
            if (!contentHtml && !(field.hint && String(field.hint).trim())) {
              return null;
            }
            return (
              <div
                key={`${convertLabelIntoName(field.type)}-${i}`}
                className={`col-span-12 flex flex-col`}
              >
                {contentHtml && (
                  <div
                    className='form-content-block'
                    dangerouslySetInnerHTML={{
                      __html: contentHtml,
                    }}
                  />
                )}
                {field.hasOwnProperty("hint") && field.hint && (
                    <div
                        className='text-xs text-gray-600 rounded-lg px-2 py-2'
                        dangerouslySetInnerHTML={{
                          __html: field.hint,
                        }}
                    />
                )}
              </div>
            );
          } else if (field.type === "textarea" && checkIsDependent(field))
          {
            if (isReadOnlyView) {
              return (
                <FormReadOnlyField
                  key={`${inputKey}-${i}`}
                  label={field.lable}
                  value={field.value}
                  fullWidth={parseBooleanVal(field.isFullWidth)}
                  hint={field.hint}
                  multiline
                />
              );
            }

            return (
              <div
                key={`${inputKey}-${i}`}
                className={`${
                  parseBooleanVal(field.isFullWidth)
                    ? "col-span-12"
                    : "col-span-12 md:col-span-6"
                } flex flex-col did-floating-label-content`}
              >
                <label htmlFor='' className='did-floating-label text-sm'>
                  {field.lable}
                </label>
                <textarea
                  {...(register
                    ? register(
                        `template[${index}].input_fields[${i}].${convertLabelIntoName(
                          field.lable
                        )}`,
                        {
                          required: field.required,
                          value: field.value,
                        }
                      )
                    : {
                        value: field.value,
                        readOnly: true,
                        disabled: true,
                      })}
                  type={field.type}
                  className='form-textarea'
                  rows={4}
                  placeholder={
                    previewMode ? previewFieldPlaceholder("textarea") : undefined
                  }
                  readOnly={previewMode || undefined}
                />
                {!isViewable && getError(errors, field.lable, index, i)}
                {field.hasOwnProperty("hint") && field.hint && (
                    <div
                        className='text-xs text-gray-600 rounded-lg px-2 py-2'
                        dangerouslySetInnerHTML={{
                          __html: field.hint,
                        }}
                    />
                )}
              </div>
            );
          } else if (field.type === "select" && checkIsDependent(field))
          {
            if (isReadOnlyView) {
              return (
                <FormReadOnlyField
                  key={`${inputKey}-${i}`}
                  label={field.lable}
                  value={field.value}
                  fullWidth={parseBooleanVal(field.isFullWidth)}
                  hint={field.hint}
                />
              );
            }

            return (
              <div
                key={`${inputKey}-${i}`}
                className={`${
                  parseBooleanVal(field.isFullWidth)
                    ? "col-span-12"
                    : "col-span-12 md:col-span-6"
                } flex flex-col did-floating-label-content`}
              >
                <label className='did-floating-label text-sm'>
                  {field.lable}
                </label>
                <div className='print:hidden'>
                  <select
                    {...(register
                      ? register(
                          `template[${index}].input_fields[${i}].${convertLabelIntoName(
                            field.lable
                          )}`,
                          {
                            required: field.required,
                            value: field.value,
                          }
                        )
                      : {
                          value: field.value,
                          readOnly: true,
                          disabled: true,
                        })}
                    className='did-floating-select'
                  >
                    {field.options.map((option, oi) => {
                      return (
                        <option value={option.lable} key={oi}>
                          {option.lable}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className={`did-floating-input sectionInputViewPrint`}>
                  {field.value}
                </div>
                {!isViewable && getError(errors, field.lable, index, i)}
                {field.hasOwnProperty("hint") && field.hint && (
                    <div
                        className='text-xs text-gray-600 rounded-lg px-2 py-2'
                        dangerouslySetInnerHTML={{
                          __html: field.hint,
                        }}
                    />
                )}
              </div>
            );
          } else if (field.type === "phone" && checkIsDependent(field))
          {
            if (isReadOnlyView) {
              return (
                <FormReadOnlyField
                  key={`${inputKey}-${i}`}
                  label={field.lable}
                  value={field.value}
                  fullWidth={parseBooleanVal(field.isFullWidth)}
                  hint={field.hint}
                />
              );
            }

            return (
              <div
                key={`${inputKey}-${i}`}
                className={`${
                  parseBooleanVal(field.isFullWidth)
                    ? "col-span-12"
                    : "col-span-12 md:col-span-6"
                } flex flex-col did-floating-label-content`}
              >
                <div className='did-floating-label-content'>
                  <label className='did-floating-label truncate-text text-sm'>
                    {field.lable}
                  </label>
                  <input
                    {...(register
                      ? register(
                          `template[${index}].input_fields[${i}].${convertLabelIntoName(
                            field.lable
                          )}`,
                          {
                            required: parseBooleanVal(field.required),
                            // minLength: 10,
                            maxLength: 10,
                            value: field.value,
                            pattern: REGEX_PHONE_NUMBER,
                            onChange: (e) => {
                              const sanitizedValue = e.target.value.replace(
                                /\D/g,
                                ""
                              );
                              const formattedValue = sanitizedValue.slice(
                                0,
                                10
                              );
                              e.target.value = formattedValue;
                            },
                          }
                        )
                      : {
                          value: field.value,
                          readOnly: true,
                          disabled: true,
                        })}
                    className='did-floating-input appearance-no'
                    type='number'
                  />
                  {!isViewable && getError(errors, field.lable, index, i)}
                  {errors?.template?.[index]?.input_fields?.[i]?.[
                    `${field.name}`
                  ]?.type === "minLength" ||
                    (errors?.template?.[index]?.input_fields?.[i]?.[
                      `${field.name}`
                    ]?.type === "maxLength" ? (
                      <span className='text-sm text-red-500'>
                        Phone number must have 10 digits
                      </span>
                    ) : (
                      <></>
                    ))}

                  {errors?.template?.[index]?.input_fields?.[i]?.[
                    `${field.name}`
                  ]?.type === "pattern" && (
                    <span className='text-sm text-red-500'>
                      {/* Phone number must start with 04, 1300 or 1800 */}
                      Please enter a valid phone number!
                    </span>
                  )}
                  {field.hasOwnProperty("hint") && field.hint && (
                      <div
                          className='text-xs text-gray-600 rounded-lg px-2 py-2'
                          dangerouslySetInnerHTML={{
                            __html: field.hint,
                          }}
                      />
                  )}
                </div>
              </div>
            );
          } else if (field.type === "email" && checkIsDependent(field))
          {
            if (isReadOnlyView) {
              return (
                <FormReadOnlyField
                  key={`${inputKey}-${i}`}
                  label={field.lable}
                  value={field.value}
                  fullWidth={parseBooleanVal(field.isFullWidth)}
                  hint={field.hint}
                />
              );
            }

            return (
              <div
                key={`${inputKey}-${i}`}
                className={`${
                  parseBooleanVal(field.isFullWidth)
                    ? "col-span-12"
                    : "col-span-12 md:col-span-6"
                } flex flex-col did-floating-label-content`}
              >
                <label className='did-floating-label text-sm'>
                  {field.lable}
                </label>
                <input
                  {...(register
                    ? register(
                        `template[${index}].input_fields[${i}].${convertLabelIntoName(
                          field.lable
                        )}`,
                        {
                          value: field.value,
                          required: parseBooleanVal(field.required),
                          pattern: EMAIL_REGEX_EXP,
                        }
                      )
                    : {
                        value: field.value,
                        readOnly: true,
                        disabled: true,
                      })}
                  className='did-floating-input'
                  type='email'
                />
                {!isViewable && getError(errors, field.lable, index, i)}
                {errors?.template?.[index]?.input_fields?.[i]?.[`${field.name}`]
                  ?.type === "pattern" && (
                  <span className='text-sm text-red-500'>
                    Please Enter a valid email
                  </span>
                )}
                {field.hasOwnProperty("hint") && field.hint && (
                    <div
                        className='text-xs text-gray-600 rounded-lg px-2 py-2'
                        dangerouslySetInnerHTML={{
                          __html: field.hint,
                        }}
                    />
                )}
              </div>
            );
          } else if (field.type === "address" && checkIsDependent(field))
          {
           
            return (
              <div
                key={`${inputKey}-${i}`}
                className={`col-span-12 flex flex-col did-floating-label-content`}
              >
                <div className='grid grid-cols-4 space-x-4'>
                  <div className='col-span-2 md:col-span-1 did-floating-label-content'>
                    <label className='did-floating-label text-sm'>
                      Street Name
                    </label>
                    <div className='print:hidden'>
                      <input
                        {...(register
                          ? register(
                              `template[${index}].input_fields[${i}].address.street`,
                              {
                                required: field.required,
                                value: field.hasOwnProperty("address")
                                  ? field.address?.street
                                  : "",
                              }
                            )
                          : {
                              value: field.hasOwnProperty("address")
                                ? field.address?.street
                                : "",
                              readOnly: true,
                              disabled: true,
                            })}
                        className='did-floating-input'
                        type='text'
                      />
                      {errors?.template?.[index]?.input_fields?.[i]?.address
                        ?.street?.type === "required" && (
                        <span className='text-sm text-red-500'>
                          This field is required!
                        </span>
                      )}
                    </div>
                    <div className={`did-floating-input sectionInputViewPrint`}>
                      {field.hasOwnProperty("address")
                        ? field.address?.street
                        : ""}
                    </div>
                  </div>
                  <div className='col-span-2 md:col-span-1'>
                    <label className='did-floating-label text-sm'>Suburb</label>
                    <div className='print:hidden'>
                      <input
                        {...(register
                          ? register(
                              `template[${index}].input_fields[${i}].address.subrub`,
                              {
                                required: field.required,
                                value: field.hasOwnProperty("address")
                                  ? field.address?.subrub
                                  : "",
                              }
                            )
                          : {
                              value: field.hasOwnProperty("address")
                                ? field.address?.subrub
                                : "",
                              readOnly: true,
                              disabled: true,
                            })}
                        className='did-floating-input'
                        type='text'
                      />
                      {errors?.template?.[index]?.input_fields?.[i]?.address
                        ?.subrub?.type === "required" && (
                        <span className='text-sm text-red-500'>
                          This field is required!
                        </span>
                      )}
                    </div>
                    <div className={`did-floating-input sectionInputViewPrint`}>
                      {field.hasOwnProperty("address")
                        ? field.address?.subrub
                        : ""}
                    </div>
                  </div>
                  <div className='col-span-2 md:col-span-1'>
                    <label className='did-floating-label text-sm'>
                      Postcode
                    </label>
                    <input
                      {...(register
                        ? register(
                            `template[${index}].input_fields[${i}].address.postcode`,
                            {
                              required: field.required,
                              value: field.hasOwnProperty("address")
                                ? field.address?.postcode
                                : "",
                              onChange: (e) => {
                                e.target.value = e.target.value
                                  .replace(/[^0-9]/g, "")
                                  .slice(0, 4);
                              },
                              maxLength: 4,
                              minLength: 4,
                            }
                          )
                        : {
                            value: field.hasOwnProperty("address")
                              ? field.address?.postcode
                              : "",
                            readOnly: true,
                            disabled: true,
                          })}
                      maxLength={4}
                      minLength={4}
                      className='did-floating-input'
                      type='number'
                    />
                    {errors?.template?.[index]?.input_fields?.[i]?.address
                      ?.postcode?.type === "required" && (
                      <span className='text-sm text-red-500'>
                        This field is required!
                      </span>
                    )}
                    {(errors?.template?.[index]?.input_fields?.[i]?.address
                      ?.postcode?.type === "maxLength" ||
                      errors?.template?.[index]?.input_fields?.[i]?.address
                        ?.postcode?.type === "minLength") && (
                      <span className='text-sm text-red-500'>
                        Postcode must be 4 digits!
                      </span>
                    )}
                  </div>

                  <div className='col-span-2 md:col-span-1'>
                    <label className='did-floating-label text-sm'>State</label>
                    <select
                      className='did-floating-select'
                      {...(register
                        ? register(
                            `template[${index}].input_fields[${i}].address.state`,
                            {
                              required: field.required,
                              value: field.hasOwnProperty("address")
                                ? field.address?.state
                                : "New south Wales",
                            }
                          )
                        : {
                            value: field.hasOwnProperty("address")
                              ? field.address?.state
                              : "",
                            readOnly: true,
                            disabled: true,
                          })}
                    >
                      {STATE_FULL_LABEL_OPTIONS.map((option, state_index) => (
                        <option
                          key={`${state_index}-${option.value}`}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>

                    {errors?.template?.[index]?.input_fields?.[i]?.address
                      ?.state?.type === "required" && (
                      <span className='text-sm text-red-500'>
                        This field is required!
                      </span>
                    )}
                  </div>
                </div>
                {field.hasOwnProperty("hint") && field.hint && (
                    <div
                        className='text-xs text-gray-600 rounded-lg px-2 py-2'
                        dangerouslySetInnerHTML={{
                          __html: field.hint,
                        }}
                    />
                )}
              </div>
            );
          } else if (field.type === "signature" && checkIsDependent(field))
          {
            const signatureKey = field.name;
            const storedSignature = findStoredFormSignature(
              signatures,
              signatureKey,
              inputKey
            );
            const sigImage = signatureImageSrc(storedSignature?.image);
            const signatureValidationKey =
              inputKey === signatureKey ? `${signatureKey}__signed` : inputKey;

            if (isReadOnlyView) {
              return (
                <FormReadOnlySignature
                  key={`${signatureKey || inputKey}-${i}`}
                  label={field.lable}
                  imageSrc={sigImage}
                />
              );
            }

            if (previewMode) {
              return (
                <div
                  key={`${signatureKey || inputKey}-${i}`}
                  className={`${
                    parseBooleanVal(field.isFullWidth)
                      ? "col-span-12"
                      : "col-span-12 md:col-span-6"
                  } form-signature-block flex flex-col`}
                >
                  <label className="did-floating-label text-sm">{field.lable}</label>
                  <div className="form-signature-display-inner">
                    <div className="form-signature-empty">Sign here</div>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={`${inputKey}-${i}`}
                className={`${
                    parseBooleanVal(field.isFullWidth)
                        ? "col-span-12"
                        : "col-span-12 md:col-span-6"
                } form-signature-block flex flex-col mb-2`}
              >
                <div className='flex flex-col pb-2'>
                  <p className='form-readonly-label'>Signature</p>
                </div>
                <div className='flex flex-col lg:flex-row justify-between items-center'>
                  <div className=' flex flex-col text-gray-500 py-2 '>
                    <p className='did-floating-label text-sm pb-2'>
                      {field.lable}
                    </p>
                    {sigImage ? (
                      <>
                        <img
                          src={sigImage}
                          alt='signature'
                          className='h-auto w-auto sigCanvas border rounded-lg bg-gray-100'
                        />
                        {register && parseBooleanVal(field.required) && (
                          <input
                            type='hidden'
                            {...register(`${signatureValidationKey}`, {
                              required: parseBooleanVal(field.required),
                            })}
                            defaultValue='signature-added'
                          />
                        )}
                      </>
                    ) : (
                      register && (
                        <>
                          <Controller
                            name={field.name}
                            control={control}
                            render={({ field: controllerField }) => (
                              <SignatureCanvas
                                ref={(ef) =>
                                  assignSignatureCanvasRef(
                                    sigCanvasManager,
                                    signatureKey,
                                    ef
                                  )
                                }
                                onEnd={() => {
                                  const payload = formatIntoPngManager(
                                    signatureKey,
                                    index,
                                    i,
                                    null,
                                    inputKey
                                  );
                                  if (!payload?.image) return;
                                  controllerField.onChange(payload);
                                  if (setValue) {
                                    setValue(
                                      signatureValidationKey,
                                      "signature-added",
                                      { shouldValidate: true }
                                    );
                                  }
                                }}
                                penColor='green'
                                canvasProps={{
                                  width: "auto",
                                  height: "auto",
                                  className:
                                    "h-auto w-auto sigCanvas border rounded-lg bg-gray-100",
                                }}
                              />
                            )}
                          />

                          <input
                            className='hidden sr-only'
                            {...register(`${signatureValidationKey}`, {
                              required: parseBooleanVal(field.required),
                            })}
                          />

                          {errors?.[`${signatureValidationKey}`]
                            ?.type === "required" && (
                            <span className='text-sm text-red-500'>
                              This field is required!
                            </span>
                          )}
                        </>
                      )
                    )}
                    {!isViewable && (
                      <div
                        onClick={() =>
                          clearSignatureManager(
                            signatureKey,
                            signatureValidationKey,
                            inputKey
                          )
                        }
                      >
                        <p className='flex items-center mt-1 cursor-pointer'>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='h-6 w-6'
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth='2'
                              d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                            />
                          </svg>
                          {storedSignature ? "update" : "clear"}
                        </p>
                      </div>
                    )}
                    {field.hasOwnProperty("hint") && field.hint && (
                        <div
                            className='text-xs text-gray-600 rounded-lg px-2 py-2'
                            dangerouslySetInnerHTML={{
                              __html: field.hint,
                            }}
                        />
                    )}
                  </div>
                </div>
              </div>
            );
          } else if (field.type === "table" && checkIsDependent(field))
          {
            if (isReadOnlyView) {
              return (
                <FormReadOnlyTable
                  key={`${inputKey}-${i}`}
                  label={field.lable}
                  columns={field.columns ?? []}
                  rows={field.rows ?? []}
                  hint={field.hint}
                />
              );
            }

            const hasRows =
              field.hasOwnProperty("rows") && field.rows.length > 0;

            const tableError = getRequiredTableError(
              watchFormData?.template?.[index]?.input_fields?.[i],
              field.columns
            );
            const isWideTable = (field?.columns?.length ?? 0) >= 7;
            const wideTableClass = isWideTable
              ? " form-dynamic-table--wide"
              : "";
            return (
              <div
                key={`${inputKey}-${i}`}
                className='form-dynamic-table-field flex flex-col min-w-0 max-w-full w-full'
              >
                <label
                  htmlFor=''
                  className='did-floating-label truncate-text text-sm'
                >
                  {field.lable}
                </label>
                <div className='flex flex-col min-w-0 max-w-full w-full'>
                  <div className='form-table-wrapper mt-2 max-h-[420px] overflow-auto custom-scroll w-full min-w-0'>
                    <table
                      className={`form-table form-dynamic-table form-table-cards print:table-fixed${wideTableClass}`}
                    >
                      <thead>
                        <tr>
                          {!isViewable && (
                            <th scope='col' className='form-table-row-index print:hidden'>
                              #
                            </th>
                          )}
                          {field?.columns.map((item, a) => (
                            <th
                              key={`${convertLabelIntoName(item.lable)}-${a}`}
                              scope='col'
                              className='table-col-min-width'
                            >
                              {item.lable}
                            </th>
                          ))}
                          {!isViewable && (
                            <th
                              scope='col'
                              className='form-table-actions-col print:hidden'
                            >
                              Remove
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {!hasRows && !isViewable ? (
                          <tr className='form-table-empty-row'>
                            <td colSpan={(field?.columns?.length || 0) + 2}>
                              <div className='form-table-empty-state'>
                                <p>No line items yet</p>
                                <span className='form-table-empty-cta'>
                                  Use &ldquo;Add row&rdquo; to add your first entry
                                </span>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          field?.rows.map((row, ri) => {
                            return (
                              <tr key={ri}>
                                {!isViewable && (
                                  <td className='form-table-row-index print:hidden'>
                                    {ri + 1}
                                  </td>
                                )}
                                {row?.map((col, ci) => {
                                  if (col.type === "select") {
                                    return (
                                      <td
                                        key={`${convertLabelIntoName(
                                          col.lable
                                        )}-${ci}`}
                                        data-label={col.lable}
                                        className='table-col-min-width'
                                      >
                                        <select
                                          {...(register
                                            ? register(
                                                `template[${index}].input_fields[${i}].rows[${ri}].columns[${ci}].${convertLabelIntoName(
                                                  col.lable
                                                )}`,
                                                {
                                                  value: col.value,
                                                }
                                              )
                                            : {
                                                value: col.value,
                                                readOnly: true,
                                                disabled: true,
                                              })}
                                          key={ci}
                                          id=''
                                          className='form-table-cell-input'
                                        >
                                          {col.options.map((option, co) => {
                                            return (
                                              <option
                                                key={co}
                                                value={option.lable}
                                              >
                                                {option.lable}
                                              </option>
                                            );
                                          })}
                                        </select>
                                      </td>
                                    );
                                  } else if (col.type === "checkbox") {
                                    return (
                                      <td
                                        key={`${convertLabelIntoName(
                                          col.lable
                                        )}-${ci}`}
                                        data-label={col.lable}
                                        className='print:break-all print:p-2 table-col-min-width print:border print:border-solid print:border-gray-200'
                                      >
                                        <div className='w-8 mx-auto'>
                                          <input
                                            {...(register
                                              ? register(
                                                  `template[${index}].input_fields[${i}].rows[${ri}].columns[${ci}].${convertLabelIntoName(
                                                    col.lable
                                                  )}`,
                                                  {
                                                    value: col.value,
                                                  }
                                                )
                                              : {
                                                  value: col.value,
                                                  readOnly: true,
                                                  disabled: true,
                                                })}
                                            style={{
                                              border: "1px solid #ebe8e8",
                                            }}
                                            type={col.type}
                                            checked={col.value}
                                            className='form-table-cell-input accent-[#4b6cb7]'
                                          />
                                        </div>
                                      </td>
                                    );
                                  } else {
                                    return (
                                      <td
                                        key={`${convertLabelIntoName(
                                          col.lable
                                        )}-${ci}`}
                                        data-label={col.lable}
                                        className='print:break-all print:p-2 table-col-min-width print:border print:border-solid print:border-gray-200'
                                      >
                                        <div className='hidden print:block text-left print:break-all'>
                                          {col.value}
                                        </div>
                                        <input
                                          {...(register
                                            ? register(
                                                `template[${index}].input_fields[${i}].rows[${ri}].columns[${ci}].${convertLabelIntoName(
                                                  col.lable
                                                )}`,
                                                {
                                                  value: col.value,
                                                }
                                              )
                                            : {
                                                value: col.value,
                                                readOnly: true,
                                                disabled: true,
                                              })}
                                          style={{
                                            border: "1px solid #ebe8e8",
                                          }}
                                          type={col.type}
                                          className='form-table-cell-input block print:hidden'
                                          step='0.01'
                                        />
                                      </td>
                                    );
                                  }
                                })}
                                {!isViewable && (
                                  <td className='form-table-actions-col print:hidden'>
                                    <button
                                      type='button'
                                      onClick={() =>
                                        delteRowHandler(index, i, ri)
                                      }
                                      className='form-table-delete-btn'
                                      aria-label={`Remove row ${ri + 1}`}
                                    >
                                      Remove
                                    </button>
                                  </td>
                                )}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className='pagebreak'></div>
                  {!isViewable && (
                    <div className='flex justify-start items-center mt-2'>
                      <button
                        type='button'
                        onClick={() => addRowHandler(index, i)}
                        className='form-table-add-row'
                      >
                        <FiPlus />
                        <span>Add row</span>
                      </button>
                    </div>
                  )}
                  {!isViewable && register && parseBooleanVal(field.required) && (
                    <>
                      <input
                        type='text'
                        readOnly
                        tabIndex={-1}
                        className='sr-only'
                        {...register(
                          `template[${index}].input_fields[${i}].${TABLE_REQUIRED_FIELD_NAME}`,
                          {
                            validate: () => (tableError ? tableError : true),
                          }
                        )}
                      />
                      {tableError &&
                        errors?.template?.[index]?.input_fields?.[i]?.[
                          TABLE_REQUIRED_FIELD_NAME
                        ] && (
                          <span className='text-sm text-red-500 text-left'>
                            {tableError}
                          </span>
                        )}
                    </>
                  )}
                </div>
                {field.hasOwnProperty("hint") && field.hint && (
                    <div
                        className='text-xs text-gray-600 rounded-lg px-2 py-2'
                        dangerouslySetInnerHTML={{
                          __html: field.hint,
                        }}
                    />
                )}
              </div>
            );
          } else if (field.type === "date" && checkIsDependent(field))
          {
            if (isReadOnlyView) {
              return (
                <FormReadOnlyField
                  key={`${inputKey}-${i}`}
                  label={field.lable}
                  value={field.value}
                  fullWidth={parseBooleanVal(field.isFullWidth)}
                  hint={field.hint}
                />
              );
            }

            return (
              <div
                key={`${inputKey}-${i}`}
                className={`${
                  parseBooleanVal(field.isFullWidth)
                    ? "col-span-12"
                    : "col-span-12 md:col-span-6"
                } flex flex-col did-floating-label-content`}
              >
                <label htmlFor='' className='did-floating-label text-sm'>
                  {field.lable}
                </label>
                <input
                  {...(register
                    ? register(
                        `template[${index}].input_fields[${i}].${convertLabelIntoName(
                          field.lable
                        )}`,
                        {
                          value: field.value,
                          required: parseBooleanVal(field.required),
                          pattern: dynamicFormDateValidation(
                            field,
                            watchFormData,
                            index,
                            i,
                            null
                          ),
                        }
                      )
                    : {
                        value: field.value,
                        readOnly: true,
                        disabled: true,
                      })}
                  type={field.type}
                  // max={completeDate}
                  className='did-floating-input'
                  placeholder={
                    previewMode ? previewFieldPlaceholder("date") : undefined
                  }
                  readOnly={previewMode || undefined}
                />
                {!isViewable && getError(errors, field.lable, index, i)}
                {errors?.template?.[index]?.input_fields?.[i]?.[
                  `${inputKey}`
                ]?.type === "pattern" && (
                  <span className='text-sm text-red-500'>
                    {dynamicFormDateValidation(
                      field,
                      watchFormData,
                      index,
                      i,
                      null,
                      false
                    )}
                  </span>
                )}
                {field.hasOwnProperty("hint") && field.hint && (
                    <div
                        className='text-xs text-gray-600 rounded-lg px-2 py-2'
                        dangerouslySetInnerHTML={{
                          __html: field.hint,
                        }}
                    />
                )}
              </div>
            );
          } else if (field.type === "checkbox" && checkIsDependent(field))
          {
            if (isReadOnlyView) {
              return (
                <FormReadOnlyChoice
                  key={`${inputKey}-${i}`}
                  label={field.lable}
                  selectedValue={field.value}
                  fullWidth={parseBooleanVal(field.isFullWidth)}
                  hint={field.hint}
                  type='checkbox'
                />
              );
            }

            const checkboxId = `template[${index}].input_fields[${i}].${convertLabelIntoName(
              field.lable
            )}`;

            return (
              <div
                key={`${inputKey}-${i}`}
                className={`${
                  parseBooleanVal(field.isFullWidth)
                    ? "col-span-12"
                    : "col-span-12 md:col-span-6"
                }`}
              >
                <div className='form-checkbox-card'>
                  <input
                    {...register(
                      `template[${index}].input_fields[${i}].${convertLabelIntoName(
                        field.lable
                      )}`,
                      {
                        required: parseBooleanVal(field.required),
                        value: field.value,
                      }
                    )}
                    type='checkbox'
                    id={checkboxId}
                  />
                  <label htmlFor={checkboxId}>{field.lable}</label>
                </div>
                {field.hasOwnProperty("hint") && field.hint && (
                  <div
                    className='form-field-hint'
                    dangerouslySetInnerHTML={{ __html: field.hint }}
                  />
                )}
                {!isViewable && getError(errors, field.lable, index, i)}
              </div>
            );
          } else if (field.type === "radio" && checkIsDependent(field))
          {
            if (isReadOnlyView) {
              return (
                <FormReadOnlyChoice
                  key={`${inputKey}-${i}`}
                  label={field.lable}
                  options={field.options}
                  selectedValue={field.value}
                  fullWidth={parseBooleanVal(field.isFullWidth)}
                  hint={field.hint}
                  type='radio'
                />
              );
            }

            const radioFieldName = `template[${index}].input_fields[${i}].${convertLabelIntoName(
              field.lable
            )}`;

            return (
              <div
                key={`${inputKey}-${i}`}
                className={`${
                  parseBooleanVal(field.isFullWidth)
                    ? "col-span-12"
                    : "col-span-12 md:col-span-6"
                } flex flex-col did-floating-label-content`}
              >
                <label className='did-floating-label text-sm'>
                  {field.lable}
                </label>
                <div className='form-radio-pills'>
                  {field.options.map((option, oi) => {
                    const optionId = `${radioFieldName}.${option.lable}`;
                    return (
                      <div key={oi} className='form-radio-pill'>
                        <input
                          {...register(radioFieldName, {
                            required: parseBooleanVal(field.required),
                            value: !field.hasOwnProperty("value")
                              ? null
                              : field.value
                              ? field.value
                              : null,
                          })}
                          value={option.lable}
                          id={optionId}
                          type='radio'
                        />
                        <label htmlFor={optionId}>{option.lable}</label>
                      </div>
                    );
                  })}
                </div>
                {!isViewable && getError(errors, field.lable, index, i)}
                {field.hasOwnProperty("hint") && field.hint && (
                  <div
                    className='form-field-hint'
                    dangerouslySetInnerHTML={{ __html: field.hint }}
                  />
                )}
              </div>
            );
          } else if (field.type === "file" && checkIsDependent(field))
          {
            return (
              <div
                key={`${inputKey}-${i}`}
                className={`${
                  parseBooleanVal(field.isFullWidth)
                    ? "col-span-12"
                    : "col-span-12 md:col-span-6"
                } flex flex-col did-floating-label-content`}
              >
                <label className='did-floating-label text-sm'>
                  {field.lable}
                </label>
                {register === null && isViewable && (
                  <div className='mt-1'>
                    <p className='text-sm text-gray-500 truncate'>
                      {field.value}
                    </p>
                    <a
                      href={field.path}
                      download
                      target='_blank'
                      className='inline-block mt-1 cursor-pointer bg-gradient text-white font-bold py-1 px-4 rounded print:hidden'
                      rel='noreferrer'
                    >
                      {`${getExtensionName(field.path)}`}
                    </a>
                  </div>
                )}
                {!isViewable && register !== null && (
                  <>
                    <input
                      {...(register
                        ? register(
                            `template[${index}].input_fields[${i}].${convertLabelIntoName(
                              field.lable
                            )}`,
                            {
                              required:
                                parseBooleanVal(field.required) && !field.value
                                  ? true
                                  : false,
                              onChange: (e) => {
                                fileUploadHandler(e, index, i);
                              },
                              value: field.value
                                ? [new File([Blob], "file")]
                                : null,
                            }
                          )
                        : {
                            value: field.value
                              ? [new File([Blob], "file")]
                              : null,
                            readOnly: true,
                            disabled: true,
                          })}
                      type='file'
                      id={`template[${index}].input_fields[${i}].${field.name}`}
                      style={{ display: "none" }}
                    />
                    <div className='did-floating-input'>
                      <div className='flex justify-between items-center h-full'>
                        <label
                          htmlFor={`template[${index}].input_fields[${i}].${field.name}`}
                          className='cursor-pointer bg-gradient p-1 rounded text-white p-0.5'
                        >
                          {field.value
                            ? "Change file"
                            : "Click me to upload file"}
                        </label>
                        {field.value && (
                          <p
                            style={{ whiteSpace: "pre" }}
                            className='truncate pl-3'
                          >
                            {field.value}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}
                {!isViewable && getError(errors, field.lable, index, i)}
                {!fileUploadChecker && fileLoader === `${index}-${i}` && (
                  <h1>Loading</h1>
                )}
                {field.hasOwnProperty("hint") && field.hint && (
                    <div
                        className='text-xs text-gray-600 rounded-lg px-2 py-2'
                        dangerouslySetInnerHTML={{
                          __html: field.hint,
                        }}
                    />
                )}
              </div>
            );
          } else if (field.type === "sub_section")
          {
            return (
              <SubSectionInputFields
                key={`${i}-${index}-${field.section_title}`}
                isViewable={isViewable}
                previewMode={previewMode}
                field={field}
                i={i}
                register={register}
                index={index}
                errors={errors}
                fileUploadHandler={fileUploadHandler}
                fileUploadChecker={fileUploadChecker}
                fileLoader={fileLoader}
                signatures={signatures}
                control={control}
                clearSignatureManager={clearSignatureManager}
                sigCanvasManager={sigCanvasManager}
                formatIntoPngManager={formatIntoPngManager}
                watchFormData={watchFormData}
                setSelectedLegal={setSelectedLegal}
                addRowHandler={addRowHandler}
                delteRowHandler={delteRowHandler}
                handleRemoveOther={handleRemoveOther}
                handleAddOther={handleAddOther}
                checkIsDependent={checkIsDependent}
                setValue={setValue}
              />
            );
          } else
          {
            const isParticipantNameField = isExactParticipantNameField(field);
            const safeParticipantName = participant
              ? `${participant.first_name ?? ""} ${participant.last_name ?? ""}`.trim()
              : "";
            const participantDisplayValue = isParticipantNameField
              ? capitalizeParticipantName(
                  templateForm?.submitted_against ?? safeParticipantName ?? ""
                )
              : "";
            const textFieldDisplayValue = isParticipantNameField
              ? participantDisplayValue
              : (updatedBody ?? field.value ?? "");

            return (
              <>
                {checkIsDependent(field) && (
                  <>
                    {isReadOnlyView ? (
                      <FormReadOnlyField
                        key={`${inputKey}-${i}`}
                        label={field.lable}
                        value={textFieldDisplayValue}
                        fullWidth={parseBooleanVal(field.isFullWidth)}
                        hint={field.hint}
                        className={
                          parseBooleanVal(field.isBodyModal)
                            ? "col-span-8"
                            : ""
                        }
                      />
                    ) : (
                    <div
                      key={`${inputKey}-${i}`}
                      className={`${
                        parseBooleanVal(field.isFullWidth) &&
                        parseBooleanVal(field.isBodyModal)
                          ? "col-span-8"
                          : parseBooleanVal(field.isFullWidth)
                          ? "col-span-12"
                          : "col-span-12 md:col-span-6"
                      } flex flex-col did-floating-label-content `}
                    >
                      <label className='did-floating-label truncate-text text-sm'>
                        {field.lable}
                      </label>
                      <div className='mt-1 rounded-md'>
                        <div className='print:hidden'>
                          <input
                            title={textFieldDisplayValue}
                            {...(register
                              ? register(
                                  `template[${index}].input_fields[${i}].${resolveDynamicFieldKey(
                                    field,
                                    i
                                  )}`,
                                  {
                                    required: parseBooleanVal(field.required),
                                    value: isParticipantNameField
                                      ? participantDisplayValue
                                      : updatedBody
                                      ? updatedBody
                                      : field.value,
                                  }
                                )
                              :                               {
                                  value: isParticipantNameField
                                    ? participantDisplayValue
                                    : updatedBody
                                    ? updatedBody
                                    : field.value,
                                  readOnly: true,
                                  disabled: true,
                                })}
                            disabled={
                              parseBooleanVal(field.isBodyModal) ||
                              isParticipantNameField
                            }
                            type={field.type}
                            step={field.type === "number" ? "0.01" : undefined} // allows two decimal places for number type
                            className={`${
                              parseBooleanVal(field.isBodyModal) && "capitalize"
                            } did-floating-input `}
                            placeholder={
                              previewMode
                                ? previewFieldPlaceholder(field.type)
                                : undefined
                            }
                            readOnly={previewMode || undefined}
                          />
                        </div>

                        <div
                          className={`${
                            parseBooleanVal(field.isBodyModal) && "capitalize"
                          } did-floating-input sectionInputViewPrint`}
                        >
                          {isParticipantNameField
                            ? participantDisplayValue
                            : updatedBody
                            ? updatedBody
                            : (field.value ?? "")}
                        </div>

                        {field?.legalRef && (
                          <div className='absolute right-[10px] top-[37px]'>
                            <span
                              onClick={() => setSelectedLegal(field.legalRef)}
                              role='button'
                              tabIndex='0'
                              className='cursor-pointer'
                            >
                              <QuestionMarkCircleIcon
                                className='h-5 w-5 text-gray-400'
                                aria-hidden='true'
                              />
                            </span>
                          </div>
                        )}
                      </div>
                      {field.hasOwnProperty("hint") && field.hint && (
                        <div
                          className='text-xs text-gray-600 rounded-lg px-2 py-2'
                          dangerouslySetInnerHTML={{
                            __html: field.hint,
                          }}
                        />
                      )}
                      {isParticipantNameField && (
                        <div className='bg-gray-100 rounded-lg px-4'>
                          {isParticipantNameField &&
                            participantList.length > 0 &&
                            participantList.map((pl) => {
                              const fn = pl?.first_name ?? "";
                              const ln = pl?.last_name ?? "";
                              const fullName =
                                capitalizeParticipantName(`${fn} ${ln}`.trim()) ||
                                "—";
                              return (
                                <div key={pl?.id ?? fn + ln} className='flex items-center space-x-3 p-1'>
                                  <div className='h-6 w-6 font-bold rounded-full'>
                                    <img
                                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=random&size=128`}
                                      alt=''
                                      className='w-full rounded-full font-bold'
                                    />
                                  </div>
                                  <div>{fullName}</div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                      {!isViewable && getError(errors, field.lable, index, i)}
                    </div>
                    )}
                    {isViewable &&
                      field?.isBodyModal &&
                      parseBooleanVal(field.isBodyModal) && (
                        <div className='col-span-2 flex items-center did-floating-label-content print:hidden'>
                          <CustomButton
                            label='View'
                            type='button'
                            cssClass='mb-2 text-white py-2 px-4 rounded-lg bg-blue-dark h-fit'
                            clickHandler={() => {
                              setSelectedBody(field?.value);
                              setOpenBodyModal(true);
                            }}
                          />
                        </div>
                      )}
                    {!isViewable &&
                      setFieldName &&
                      field?.isBodyModal &&
                      parseBooleanVal(field.isBodyModal) && (
                        <div className='col-span-2 flex items-center did-floating-label-content print:hidden'>
                          <CustomButton
                            cssClass='mb-2 text-white py-2 px-4 rounded-lg bg-blue-dark h-fit'
                            label='Update'
                            type='button'
                            varinat='primary'
                            clickHandler={() => {
                              setFieldName(
                                `template[${index}].input_fields[${i}].${convertLabelIntoName(
                                  field.lable
                                )}`
                              );
                              setSelectedBody(field?.value);
                              setOpenBodyModal(true);
                            }}
                          />
                        </div>
                      )}
                    {!isViewable &&
                      setFieldName === null &&
                      field?.isBodyModal &&
                      parseBooleanVal(field.isBodyModal) && (
                        <div className='col-span-2 flex items-center did-floating-label-content print:hidden'>
                          <CustomButton
                            type='button'
                            clickHandler={() => {
                              const element = document.getElementById(
                                `template[${index}].input_fields[${i}].${convertLabelIntoName(
                                  field.lable
                                )}`
                              );
                              const obj = {
                                index,
                                input_field_index: i,
                                name: field.name,
                                field,
                                value:
                                  element && element.value ? element.value : "",
                              };
                              setSelectedBody(obj);
                              setOpenBodyModal(true);
                            }}
                            cssClass='mb-2 text-white py-2 px-4 rounded-lg bg-blue-dark h-fit'
                            label='Select Body'
                          />
                        </div>
                      )}
                  </>
                )}
              </>
            );
          }
        })}
    </>
  );
};

export default SectionInputFields;
